// Normaliza flags que llegan mezclados desde backend.
export const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value
  if (typeof value === "string") return value.toLowerCase() === "true"
  if (typeof value === "number") return value === 1
  return fallback
}

// Genera ids estables para React y para referencias internas del arbol.
export const buildNodeId = (prefix, item, fallback = "unknown") => {
  const rawKey = String(item?.key || item?.llave || item?.id || item?.name || fallback)
  return `${prefix}-${rawKey.replace(/[^a-zA-Z0-9_-]/g, "_")}`
}

// Unifica el formato visible del nombre del modelo.
export const formatModelName = (modelName = "") => {
  return String(modelName || "").trim().toLowerCase().replace(/-/g, " ")
}

const invalidAssignmentNodeValues = new Set(["-", "--", "---", "null", "undefined", "n/a", "na", "s/d", "sin dato", "sin datos", "none"])

// Limpia nombres de nodo asignado que llegan vacios o con placeholders del backend.
export const normalizeAssignmentNodeName = (nodeName = "") => {
  const normalizedNodeName = String(nodeName ?? "").trim()
  if (!normalizedNodeName) return ""
  if (invalidAssignmentNodeValues.has(normalizedNodeName.toLowerCase())) return ""
  if (!/[a-z0-9]/i.test(normalizedNodeName)) return ""
  return normalizedNodeName
}

// Traduce el producto crudo a la etiqueta de modelo usada por la pantalla.
export const getModelFromProduct = (productName = "") => {
  return formatModelName(productName)
}

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24

// Calcula dias restantes de prueba a partir de la fecha de expiracion.
export const getTrialAvailability = (cloudExpiration) => {
  if (!cloudExpiration) {
    return { remainingDays: undefined, trialExpired: false }
  }

  const expirationDate = new Date(cloudExpiration)
  if (Number.isNaN(expirationDate.getTime())) {
    return { remainingDays: undefined, trialExpired: false }
  }

  const remainingTime = expirationDate.getTime() - Date.now()
  if (remainingTime <= 0) {
    return { remainingDays: 0, trialExpired: true }
  }

  return {
    remainingDays: Math.ceil(remainingTime / MILLISECONDS_PER_DAY),
    trialExpired: false,
  }
}

// Reduce el payload del backend al shape minimo que consume esta vista.
export const normalizeDeviceItem = (item) => {
  const key = item?.key || item?.llave || ""
  const name = item?.name || item?.nombre || ""
  const assignmentNodeName = normalizeAssignmentNodeName(item?.assigmentNodeName || item?.jerarquia_nombre || "")
  const cloudStatus = item?.cloud_state || ""
  const cloudExpiration = item?.cloud_expiration || item?.cloudExpiration
  const normalizedCloudStatus = String(cloudStatus || "").toUpperCase()
  const hasAssignmentNode = Boolean(assignmentNodeName)
  const hasTrialAccess = normalizedCloudStatus.includes("TRIAL")
  const productName = item?.nombre_producto || item?.productName || item?.producto || item?.product || ""
  const { remainingDays: calculatedRemainingDays, trialExpired } = hasTrialAccess
    ? getTrialAvailability(cloudExpiration)
    : { remainingDays: undefined, trialExpired: false }

  return {
    key,
    requestKey: item?.llave || key,
    name,
    productName,
    hierarchyName: item?.hierarchyName || item?.jerarquia_nombre || name,
    empresa: item?.empresa,
    zona: item?.zona,
    jerarquia_nombre_completa: item?.jerarquia_nombre_completa,
    active: normalizeBoolean(item?.active ?? item?.activo, true),
    online: normalizeBoolean(item?.online, false),
    associatedToNode:
      typeof item?.associatedToNode === "boolean"
        ? item.associatedToNode && hasAssignmentNode
        : normalizeBoolean(item?.asignado, hasAssignmentNode) && hasAssignmentNode,
    assigmentNodeName: assignmentNodeName,
    model: getModelFromProduct(productName),
    cloudAccess: hasTrialAccess ? "trial" : "permanente",
    cloudExpiration,
    trialExpired,
    remainingDays:
      calculatedRemainingDays ??
      item?.remainingDays ??
      item?.remaining_days ??
      item?.trialRemainingDays ??
      item?.trial_remaining_days,
  }
}

// Distingue medidores padre segun la convención actual de llaves.
export const isParentKey = (key = "") => !String(key).includes("-")

// Separa base y orden a partir de la llave del medidor.
export const getKeyData = (key = "") => {
  const parts = String(key).split("-")
  if (parts.length < 2) return { base: key, order: 0 }
  return { base: parts[0], order: Number(parts[1]) || 0 }
}

// Arma el shape final de cada medidor tal como lo esperan LeftPanel y RightPanel.
export const buildMeterNode = (meter, meterId, children) => ({
  id: meterId,
  key: meter.key,
  name: meter.name,
  productName: meter.productName,
  empresa: meter.empresa,
  zona: meter.zona,
  jerarquia_nombre_completa: meter.jerarquia_nombre_completa,
  active: meter.active,
  online: meter.online,
  associatedToNode: meter.associatedToNode,
  assigmentNodeName: meter.assigmentNodeName,
  cloudAccess: meter.cloudAccess,
  cloudExpiration: meter.cloudExpiration,
  trialExpired: meter.trialExpired,
  remainingDays: meter.remainingDays,
  model: meter.model,
  requestKey: meter.requestKey,
  children,
})

// Convierte la respuesta cruda del endpoint en un arbol listo para renderizar.
export const buildDevicesFromResponse = ({ meters = [], automates = [], metersPlan = [], automatesPlan = [] }) => {
  const metersSource = meters.length ? meters : metersPlan
  const automatesSource = automates.length ? automates : automatesPlan
  const groupedMeters = new Map()
  const devices = []
  const expandedMeterIds = new Set()
  let groupIndex = 0
  for (const rawMeter of metersSource) {
    const normalizedMeter = normalizeDeviceItem(rawMeter)
    const { base, order } = getKeyData(normalizedMeter.key)
    let currentGroup = groupedMeters.get(base)
    if (!currentGroup) {
      currentGroup = { index: groupIndex, items: [] }
      groupedMeters.set(base, currentGroup)
      groupIndex += 1
    }
    currentGroup.items.push({ meter: normalizedMeter, order })
  }
  for (const { index, items } of groupedMeters.values()) {
    items.sort((left, right) => left.order - right.order)
    let parentEntry = null
    let zeroOrderEntry = null
    for (const entry of items) {
      if (parentEntry === null && isParentKey(entry.meter.key)) {
        parentEntry = entry
        break
      }
      if (zeroOrderEntry === null && entry.order === 0) {
        zeroOrderEntry = entry
      }
    }
    const resolvedParentEntry = parentEntry || zeroOrderEntry || items[0]
    const parentMeter = resolvedParentEntry.meter
    const parentMeterId = buildNodeId("medidor", parentMeter, index)
    const children = []
    let childIndex = 0
    for (const entry of items) {
      if (entry.meter.key === parentMeter.key) continue
      children.push(buildMeterNode(entry.meter, buildNodeId("medidor", entry.meter, `${index}-${childIndex}`)))
      childIndex += 1
    }
    if (children.length) {
      expandedMeterIds.add(parentMeterId)
    }
    devices.push({
      id: buildNodeId("dispositivo", parentMeter, index),
      name: parentMeter.hierarchyName || parentMeter.name,
      groups: [
        {
          id: buildNodeId("grupo", parentMeter, index),
          meters: [buildMeterNode(parentMeter, parentMeterId, children.length ? children : undefined)],
        },
      ],
    })
  }

  for (let automateIndex = 0; automateIndex < automatesSource.length; automateIndex += 1) {
    const automate = normalizeDeviceItem(automatesSource[automateIndex])
    const automateMeterId = buildNodeId("medidor-automate", automate, automateIndex)
    devices.push({
      id: buildNodeId("dispositivo-automate", automate, automateIndex),
      name: automate.name,
      groups: [
        {
          id: buildNodeId("grupo-automate", automate, automateIndex),
          meters: [buildMeterNode(automate, automateMeterId)],
        },
      ],
    })
  }
  return { devices, expandedMeterIds }
}

// Busca un medidor puntual dentro del arbol actual.
export const findMeterById = (devicesList, meterId) => {
  for (const device of devicesList) {
    for (const group of device.groups) {
      for (const meter of group.meters) {
        if (meter.id === meterId) return meter
        if (meter.children) {
          const childMatch = meter.children.find((child) => child.id === meterId)
          if (childMatch) return childMatch
        }
      }
    }
  }
  return null
}

// Busca el padre de un hijo dentro del arbol actual.
export const findParentMeterByChildId = (devicesList, childId) => {
  for (const device of devicesList) {
    for (const group of device.groups) {
      for (const meter of group.meters) {
        if (meter.children?.some((child) => child.id === childId)) return meter
      }
    }
  }
  return null
}

// Determina si un hijo queda bloqueado por el estado del padre.
export const isBlockedByParent = (devicesList, meterId) => {
  const parent = findParentMeterByChildId(devicesList, meterId)
  return Boolean(parent && !parent.active && parent.cloudAccess === "sin-acceso")
}

// Actualiza un medidor puntual dentro del arbol y mantiene el shape original.
export const updateMeterInDevices = (devices, meterId, updates) =>
  devices.map((device) => ({
    ...device,
    groups: device.groups.map((group) => ({
      ...group,
      meters: group.meters.map((meter) => {
        if (meter.id === meterId) {
          return {
            ...meter,
            ...updates,
            children: meter.children ? meter.children.map((child) => ({ ...child, ...updates })) : meter.children,
          }
        }
        if (meter.children) {
          return {
            ...meter,
            children: meter.children.map((child) =>
              child.id === meterId ? { ...child, ...updates } : child
            ),
          }
        }
        return meter
      }),
    })),
  }))

// Elimina un medidor o hijo del arbol conservando solo ramas validas.
export const removeMeterFromDevices = (devices, meterId) =>
  devices
    .map((device) => ({
      ...device,
      groups: device.groups
        .map((group) => ({
          ...group,
          meters: group.meters
            .map((meter) => {
              if (meter.id === meterId) return null
              if (meter.children?.length) {
                const nextChildren = meter.children.filter((child) => child.id !== meterId)
                return nextChildren.length === meter.children.length
                  ? meter
                  : {
                    ...meter,
                    children: nextChildren.length ? nextChildren : undefined,
                  }
              }
              return meter
            })
            .filter(Boolean),
        }))
        .filter((group) => group.meters.length > 0),
    }))
    .filter((device) => device.groups.length > 0)

// Construye las opciones del filtro de producto a partir del arbol actual.
export const buildProductFilterOptions = (devices) =>
  Array.from(
    new Set(
      devices.flatMap((device) =>
        device.groups.flatMap((group) =>
          group.meters.flatMap((meter) => [meter.model, ...(meter.children?.map((child) => child.model) || [])])
        )
      )
    )
  ).map((product) => ({
    value: product,
    label: formatModelName(product),
  }))

// Cuenta solo dispositivos hoja activos para el resumen superior.
export const countActiveMeters = (devices) =>
  devices.reduce(
    (total, device) =>
      total +
      device.groups.reduce(
        (groupTotal, group) =>
          groupTotal +
          group.meters.reduce(
            (meterTotal, meter) =>
              meterTotal +
              (meter.children?.length ? 0 : meter.active ? 1 : 0) +
              (meter.children?.filter((child) => child.active).length || 0),
            0
          ),
        0
      ),
    0
  )

// Aplica busqueda y filtros sin tocar la fuente principal del arbol.
export const filterDevicesTree = (devices, isFilteringDevices, matchesMeterSearch, matchesMeterFilters) => {
  if (!isFilteringDevices) return devices
  const nextDevices = []
  for (const device of devices) {
    const nextGroups = []
    for (const group of device.groups) {
      const nextMeters = []
      for (const meter of group.meters) {
        const meterMatches = matchesMeterSearch(meter) && matchesMeterFilters(meter)
        if (meter.children?.length) {
          const matchingChildren = []
          for (const child of meter.children) {
            if (matchesMeterSearch(child) && matchesMeterFilters(child)) {
              matchingChildren.push(child)
            }
          }
          if (meterMatches || matchingChildren.length) {
            nextMeters.push({
              ...meter,
              filteredChildren: matchingChildren,
            })
          }
          continue
        }
        if (meterMatches) {
          nextMeters.push(meter)
        }
      }
      if (nextMeters.length) {
        nextGroups.push({
          ...group,
          meters: nextMeters,
        })
      }
    }
    if (nextGroups.length) {
      nextDevices.push({
        ...device,
        groups: nextGroups,
      })
    }
  }
  return nextDevices
}
